import { Box, Collapse, colors } from '@mui/material';
import { useMemo, useState } from 'react';
import { SectionBox } from './helpers';
import { DependenciesTableMap, DependenciesTable } from './DependenciesTable';
import { ExpandButtonSimple } from '../../common/ExpandButtonSimple';
import { useRunOnce } from '../../common/useRunOnce';
import merge from 'lodash/merge';
import { ConfigurationType, SelectedDependencyWithClearHistoryProps } from './types';
import { NoResults } from '../shared/NoResults';
import { ActiveInactiveSelect } from '../activity-state/ActiveInactiveSelect';
import { ActiveState } from '../activity-state/helpers';
import { FetchDependencies } from './DependenciesTypes';
import { CenteredCircularProgress } from '../../common/CenteredCircularProgress';
import { useSearchContext } from './search/SearchContext';
import { PossibleSectionTitleToSectionType, BASE_SECTION_EXPANSION } from './search/consts';
import { Typography } from '@sweep-io/sweep-design';
import { useSectionExpansion } from './search/useSectionExpansion';

interface DependenciesSectionProps {
  id: string;
  metadataId: string;
  objectName: string;
  name: string;
  type?: ConfigurationType;
  configurationItems?: DependenciesTableMap;
  sweepConfigurationItems?: DependenciesTableMap;
  title: string;
  fetchDependenciesApi?: FetchDependencies;
  fetchConfigurationsApi?: (tagId: string, crmOrgId: string) => void;
  onChooseNewRule: (props: SelectedDependencyWithClearHistoryProps) => void;
  onToggleActiveInactive?: (value: ActiveState) => void;
  activeState?: ActiveState;
  crmOrgId: string;
}

export const DependenciesSection = ({
  id,
  metadataId,
  configurationItems,
  sweepConfigurationItems,
  type,
  objectName,
  name,
  title,
  fetchDependenciesApi,
  fetchConfigurationsApi,
  onChooseNewRule,
  onToggleActiveInactive,
  activeState,
  crmOrgId,
}: DependenciesSectionProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const { searchTerm, sectionExpansion } = useSearchContext();

  // Determine which section this is based on title
  const sectionKey = useMemo(() => {
    return PossibleSectionTitleToSectionType[
      title as keyof typeof PossibleSectionTitleToSectionType
    ];
  }, [title]);

  const { isExpanded, setIsExpanded } = useSectionExpansion({
    searchTerm,
    sectionExpanded: sectionKey ? sectionExpansion[sectionKey] : false,
    defaultExpanded: sectionKey ? BASE_SECTION_EXPANSION[sectionKey] : false,
  });

  const hasConfigurationItemsOtherThanObjects = useMemo(
    () =>
      configurationItems &&
      Object.values(configurationItems).some(
        (item) => item.length > 0 && item[0].parentType !== ConfigurationType.others,
      ),
    [configurationItems],
  );

  const isConfigurationItemEmpty = useMemo(
    () =>
      !isLoading &&
      ((configurationItems &&
        Object.values(configurationItems).every((item) => item.length === 0)) ||
        !configurationItems),
    [isLoading, configurationItems],
  );

  useRunOnce(async () => {
    if (isConfigurationItemEmpty && fetchDependenciesApi && type) {
      setIsLoading(true);
      setIsError(false);
      try {
        await fetchDependenciesApi({
          ruleMetadataId: metadataId,
          ruleType: type,
          crmOrgId,
          configurationName: name,
          objectName,
          metadataId,
        });

        setIsLoading(false);
      } catch (e) {
        setIsError(true);
        setIsLoading(false);
      }
    }
  });

  useRunOnce(async () => {
    if (isConfigurationItemEmpty && fetchConfigurationsApi) {
      setIsLoading(true);
      setIsError(false);
      try {
        await fetchConfigurationsApi(id, crmOrgId);
        setIsLoading(false);
      } catch (e) {
        setIsError(true);
        setIsLoading(false);
      }
    }
  });

  const items = useMemo(
    () => merge(sweepConfigurationItems, configurationItems),
    [sweepConfigurationItems, configurationItems],
  );
  const isEmpty = useMemo(
    () => (items && Object.values(items).every((item) => item.length === 0)) || !items,
    [items],
  );

  return (
    <SectionBox data-section={sectionKey || 'dependencies'}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => {
          setIsExpanded(!isExpanded);
        }}
        sx={{ cursor: 'pointer', width: '100%' }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" width="100%" pr={1}>
          <Typography variant="h5-medium">{title}</Typography>
          {onToggleActiveInactive && activeState && (
            <Box onClick={(event) => event.stopPropagation()}>
              <ActiveInactiveSelect
                withIcon
                selectedItem={activeState}
                onSelect={onToggleActiveInactive}
              />
            </Box>
          )}
        </Box>
        <ExpandButtonSimple buttonSize="tiny" isExpanded={isExpanded} />
      </Box>
      <Collapse in={isExpanded}>
        <Box pt={2} display="inline-flex" width="100%" flexDirection="column">
          {isLoading && <CenteredCircularProgress size={20} />}
          {!isLoading && isError && (
            <Typography variant="caption" color={colors.grey[700]}>
              Something went wrong, try again later
            </Typography>
          )}
          {isEmpty && !isLoading && !hasConfigurationItemsOtherThanObjects && <NoResults />}
          {!isLoading && !isEmpty && items && (
            <DependenciesTable
              crmOrgId={crmOrgId}
              onChooseNewRule={onChooseNewRule}
              configurationItems={items}
              section={sectionKey || 'dependencies'}
            />
          )}
        </Box>
      </Collapse>
    </SectionBox>
  );
};
